import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { selectors as accountListSelectors } from '../../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../../slices/auth';
import { RootState } from '../../../../slices/rootReducer';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { AccountStates } from '../../../../slices/accounts/account';
import { CurrentPlan } from '../common/currentPlan';
import { OtherPlans } from '../common/otherPlans';
import { UpgradeToProModal } from './upgradeToProModal';
import { FeatureToggle, Off, On } from '../../../../featureToggles';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { actions } from '../../../../slices/upgradeAccount/promotionalCode/index';

import styles from './index.module.scss';

interface IMapDispatchToProps {
  resetPromotionalCodeState: () => void;
}
interface LiteSubscriptionProps {
  canAddSeats: boolean;
  currentSeats?: number;
  responsesAllowed?: number;
  usage?: number | null;
  cancellationInProgress?: boolean;
  // use for Features.PlgExternal2
  responsesAllowedBeforeLapsed?: number;
  accountStatus?: AccountStates;
  // end of use for Features.PlgExternal2
}

interface LiteSubscriptionState {
  upgradeModalOpen: boolean;
}

type Props = RouteComponentProps & LiteSubscriptionProps & IMapDispatchToProps;

const isUpgradeToProPath = (pathname: string): boolean => pathname.split('/').pop() === 'upgrade-to-pro';

class LiteSubscriptionBase extends React.Component<Props, LiteSubscriptionState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      upgradeModalOpen: false
    };
    this.toggleModalOpen = this.toggleModalOpen.bind(this);
  }

  componentDidMount() {
    const { location } = this.props;
    const isUpgradeToPro = isUpgradeToProPath(location.pathname);
    if (isUpgradeToPro) {
      this.setState({ upgradeModalOpen: true });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { location, resetPromotionalCodeState } = this.props;
    const isUpgradeToPro = isUpgradeToProPath(location.pathname);
    const clickedUpgradeToPro = isUpgradeToPro && location.key !== prevProps.location.key;
    if (clickedUpgradeToPro) {
      this.setState({ upgradeModalOpen: true });
    }
    if (!this.state.upgradeModalOpen && isFeatureEnabled(Features.PromotionalCodes)) {
      resetPromotionalCodeState();
    }
  }

  private toggleModalOpen() {
    this.setState(state => ({ upgradeModalOpen: !state.upgradeModalOpen }));
  }

  private renderUpgradeModal() {
    const { upgradeModalOpen } = this.state;
    if (!upgradeModalOpen) {
      return null;
    }
    return <UpgradeToProModal onClose={this.toggleModalOpen} />;
  }

  render() {
    const { responsesAllowed, responsesAllowedBeforeLapsed, accountStatus, usage } = this.props;
    return (
      <div className={styles.subscription}>
        <div className={styles.container}>
          <div className={styles.currentPlan}>
            <FeatureToggle feature={Features.PlgExternal2}>
              <On>
                <CurrentPlan
                  accountStatus={accountStatus}
                  accountType={AccessTypes.Lite}
                  responsesAllowed={responsesAllowed}
                  responsesAllowedBeforeLapsed={responsesAllowedBeforeLapsed}
                  usage={usage}
                />
              </On>
              <Off>
                <CurrentPlan
                  accountStatus={accountStatus}
                  accountType={AccessTypes.Lite}
                  responsesAllowed={responsesAllowed}
                  usage={usage}
                />
              </Off>
            </FeatureToggle>
          </div>
          <div className={styles.otherPlans}>
            <OtherPlans
              actualPlan={AccessTypes.Lite}
              onUpgradeToPro={this.toggleModalOpen}
            />
          </div>
        </div>
        {this.renderUpgradeModal()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): LiteSubscriptionProps => {
  const user = authSelectors.getLoggedInUser(state);
  const account = accountListSelectors.getAccountById(state, user?.account.id || '');
  const usage = authSelectors.getAccountUsage(state);
  const currentSeats = account?.seatsPurchased;
  const responsesAllowed = account?.responsesAllowed;
  const responsesAllowedBeforeLapsed = account?.responsesAllowed;
  const accountStatus = account?.status;
  const cancellationInProgress = account?.subscription?.cancellationInProgress;
  return {
    canAddSeats: authSelectors.isAuthUserAdminOrOwner(state),
    currentSeats,
    responsesAllowed,
    responsesAllowedBeforeLapsed,
    accountStatus,
    usage,
    cancellationInProgress
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  resetPromotionalCodeState: actions.resetState
};

export const LiteSubscription = withRouter(connect(mapStateToProps, mapDispatchToProps)(LiteSubscriptionBase));
